import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import {
  AlertCircle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  FileText,
  MapPin,
  Maximize2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { STATUS_BEGIN, STATUS_DONE, STATUS_FAIL } from "utils/constants/others";
import { DEFAULT_DOCUMENT_OPTIONS } from "utils/constants/planShipment";
import { isImageUrl, isPdfUrl } from "utils/pdfUtils";
import {
  formatInvoiceAmount,
  formatPurchaseOrderCount,
  formatScheduleDate,
  formatScheduleGroupNumber,
  formatShippingNoteNumber,
  formatSlotDisplayName,
  formatStatus,
  formatWeight,
  formatWeightValue,
  hasShippingNote,
  partitionInvoicesByShippingNote,
} from "./planShipmentUtils";
import usePlanShipmentStore from "./usePlanShipmentStore";
import {
  BottomActions,
  CreateShippingNoteButton,
  InvoiceDocumentFallback,
  DetailContent,
  DetailHeader,
  DetailHeaderCopy,
  DetailPageFrame,
  DetailPageShell,
  DetailScrollArea,
  DetailStatus,
  DispatchedInvoiceChevron,
  DispatchedInvoiceRow,
  EditScheduleButton,
  GroupSummaryCard,
  InvoiceAction,
  InvoiceCheckbox,
  InvoiceCopy,
  InvoiceList,
  InvoiceMeta,
  InvoiceNumber,
  InvoiceRow,
  InvoiceSection,
  InvoiceSectionTitle,
  InvoiceThumbnail,
  InvoiceThumbnailImage,
  InvoiceWeightWarning,
  ModalIconWrapper,
  PurchaseOrderCard,
  PurchaseOrderGrid,
  PurchaseOrderList,
  PurchaseOrderTitle,
  RatioField,
  RatioLabel,
  RatioValue,
  ScheduledPOHeading,
  SelectableInvoiceRow,
  ShippingNoteNumber,
  SummaryIcon,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
} from "./styles.scheduleGroupDetails";
import MapLinkButton from "components/shared/MapLinkButton";
import {
  EmptyState,
  ErrorMessage,
  HeaderIconButton,
  RetryButton,
  SkeletonCard,
  SkeletonLine,
  StateTitle,
} from "./styles.planShipment";
import {
  ModalButtonRow,
  ModalCancelButton,
  ModalConfirmButton,
  ModalHandle,
  ModalInfoCircle,
  ModalOverlay,
  ModalSheet,
  ModalSubtitle,
  ModalTitle,
} from "./styles.slotSelection";

import { resolveDocumentUrl } from "utils/documentUtils";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const PAGE_SIZE = 20;

const PdfThumbnailPage = ({ url }) => {
  const [loadError, setLoadError] = useState(false);

  if (loadError) {
    return (
      <InvoiceDocumentFallback>
        <FileText aria-hidden="true" />
      </InvoiceDocumentFallback>
    );
  }

  return (
    <Document
      file={url}
      loading={null}
      onLoadError={() => setLoadError(true)}
      onSourceError={() => setLoadError(true)}
      options={DEFAULT_DOCUMENT_OPTIONS}
    >
      <Page
        pageNumber={1}
        width={68}
        renderAnnotationLayer={false}
        renderTextLayer={false}
        loading={null}
        onRenderError={() => setLoadError(true)}
      />
    </Document>
  );
};

const ScheduleGroupDetailsSkeleton = () => (
  <DetailContent aria-label="Loading PO schedule group">
    <SkeletonCard>
      <SkeletonLine $width="100%" $height="120px" />
    </SkeletonCard>
    {[0, 1].map((item) => (
      <SkeletonCard key={item}>
        <SkeletonLine $width="68%" $height="24px" />
        <SkeletonLine $height="74px" />
        <SkeletonLine $height="48px" />
      </SkeletonCard>
    ))}
  </DetailContent>
);

const InvoicePreviewThumbnail = ({ invoice }) => {
  const [imgError, setImgError] = React.useState(false);
  const invoiceNumber = invoice.invoice_number || "Invoice";
  const documentPath = invoice.invoice_image_url || invoice.invoice_path || "";
  const resolvedUrl = documentPath ? resolveDocumentUrl(documentPath) : "";
  const canOpen = Boolean(resolvedUrl);
  const showImage = isImageUrl(documentPath) && resolvedUrl && !imgError;
  const showPdf = !showImage && isPdfUrl(documentPath) && resolvedUrl;

  return (
    <InvoiceThumbnail
      href={canOpen ? resolvedUrl : undefined}
      target="_blank"
      rel="noreferrer"
      aria-label={`Open invoice ${invoiceNumber}`}
    >
      {showImage ? (
        <InvoiceThumbnailImage
          src={resolvedUrl}
          alt=""
          onError={() => setImgError(true)}
        />
      ) : showPdf ? (
        <PdfThumbnailPage url={resolvedUrl} />
      ) : (
        <InvoiceDocumentFallback>
          <FileText aria-hidden="true" />
        </InvoiceDocumentFallback>
      )}
      {canOpen ? <Maximize2 aria-hidden="true" /> : null}
    </InvoiceThumbnail>
  );
};

const InvoicePreview = ({ invoice }) => {
  const invoiceNumber = invoice.invoice_number || "Invoice";

  return (
    <>
      <InvoicePreviewThumbnail invoice={invoice} />
      <InvoiceCopy>
        <InvoiceNumber>{invoiceNumber}</InvoiceNumber>
        <InvoiceMeta>
          {formatWeightValue(invoice.number_of_skus) ?? "—"} SKUs
          {" • "}
          {formatWeightValue(invoice.weight_in_kg) ?? "—"}kg
          {" • "}
          {formatInvoiceAmount(invoice.invoice_amount)}
        </InvoiceMeta>
      </InvoiceCopy>
    </>
  );
};

const InvoiceDetails = ({ invoice }) => (
  <InvoiceRow>
    <InvoicePreview invoice={invoice} />
  </InvoiceRow>
);

const SelectableInvoiceDetails = ({ invoice, isSelected, onSelect }) => {
  const invoiceNumber = invoice.invoice_number || "Invoice";

  return (
    <SelectableInvoiceRow>
      <InvoiceCheckbox
        type="checkbox"
        checked={isSelected}
        onChange={() => onSelect(invoice.vendor_invoice_id)}
        aria-label={`Select invoice ${invoiceNumber}`}
      />
      <InvoicePreview invoice={invoice} />
    </SelectableInvoiceRow>
  );
};

const DispatchedInvoiceDetails = ({ invoice }) => {
  const navigate = useNavigate();
  const asnId = Number(invoice.asn_id);
  const canNavigate = Number.isInteger(asnId) && asnId > 0;

  return (
    <DispatchedInvoiceRow
      onClick={
        canNavigate
          ? () =>
              navigate(`/plan-shipment/shipping-notes/${asnId}`, {
                state: { asnNumber: formatShippingNoteNumber(invoice) },
              })
          : undefined
      }
      style={canNavigate ? { cursor: "pointer" } : undefined}
    >
      <InvoicePreviewThumbnail invoice={invoice} />
      <InvoiceCopy>
        <InvoiceNumber>{invoice.invoice_number || "Invoice"}</InvoiceNumber>
        <ShippingNoteNumber>
          {formatShippingNoteNumber(invoice)}
        </ShippingNoteNumber>
      </InvoiceCopy>
      <DispatchedInvoiceChevron aria-hidden="true" />
    </DispatchedInvoiceRow>
  );
};

const PurchaseOrderOverview = ({ purchaseOrder }) => (
  <>
    <PurchaseOrderTitle>
      {purchaseOrder.purchase_order_number || "Purchase order"}
    </PurchaseOrderTitle>
    <PurchaseOrderGrid>
      <RatioField>
        <RatioLabel>Net weight</RatioLabel>
        <RatioValue>
          <strong>
            {formatWeightValue(purchaseOrder.scheduled_weight_in_kg) ?? "—"}
          </strong>
          <span>/ {formatWeight(purchaseOrder.total_order_weight_in_kg)}</span>
        </RatioValue>
      </RatioField>
      <RatioField>
        <RatioLabel>No. of SKUs</RatioLabel>
        <RatioValue>
          <strong>{purchaseOrder.scheduled_sku_count ?? 0}</strong>
          <span>/ {purchaseOrder.total_sku_count ?? 0} SKUs</span>
        </RatioValue>
      </RatioField>
    </PurchaseOrderGrid>
  </>
);

const PurchaseOrderShippingNoteCard = ({
  purchaseOrder,
  selectedInvoiceIds,
  onSelectInvoice,
  editingAsnId,
}) => {
  const { availableInvoices, dispatchedInvoices } =
    partitionInvoicesByShippingNote(purchaseOrder.invoices, editingAsnId);

  if (availableInvoices.length === 0 && dispatchedInvoices.length === 0) {
    return null;
  }

  return (
    <PurchaseOrderCard>
      <PurchaseOrderOverview purchaseOrder={purchaseOrder} />

      {availableInvoices.length > 0 ? (
        <InvoiceSection>
          <InvoiceSectionTitle>
            Select invoices for a vehicle
          </InvoiceSectionTitle>
          <InvoiceList>
            {availableInvoices.map((invoice, invoiceIndex) => {
              const invoiceId = Number(invoice.vendor_invoice_id);
              return (
                <SelectableInvoiceDetails
                  key={
                    invoice.vendor_invoice_id ||
                    `${invoice.invoice_number}-${invoiceIndex}`
                  }
                  invoice={invoice}
                  isSelected={selectedInvoiceIds.has(invoiceId)}
                  onSelect={onSelectInvoice}
                />
              );
            })}
          </InvoiceList>
        </InvoiceSection>
      ) : null}

      {dispatchedInvoices.length > 0 ? (
        <InvoiceSection>
          <InvoiceSectionTitle>
            Shipping notes for dispatched invoices
          </InvoiceSectionTitle>
          <InvoiceList>
            {dispatchedInvoices.map((invoice, invoiceIndex) => (
              <DispatchedInvoiceDetails
                key={
                  invoice.vendor_invoice_id ||
                  `${invoice.invoice_number}-${invoiceIndex}`
                }
                invoice={invoice}
              />
            ))}
          </InvoiceList>
        </InvoiceSection>
      ) : null}
    </PurchaseOrderCard>
  );
};

const PurchaseOrderScheduleCard = ({ purchaseOrder, onManageInvoices }) => {
  const invoices = Array.isArray(purchaseOrder.invoices)
    ? purchaseOrder.invoices
    : [];
  const hasInvoices = invoices.length > 0;

  // Calculate total invoice weight and compare to scheduled weight
  const totalInvoiceWeight = invoices.reduce(
    (sum, inv) => sum + (Number(inv.weight_in_kg) || 0),
    0
  );
  const scheduledWeight = Number(purchaseOrder.scheduled_weight_in_kg) || 0;
  const weightShortfall =
    hasInvoices && scheduledWeight > 0
      ? scheduledWeight - totalInvoiceWeight
      : 0;
  const showWeightWarning = weightShortfall > 0;

  return (
    <PurchaseOrderCard>
      <PurchaseOrderOverview purchaseOrder={purchaseOrder} />

      {showWeightWarning ? (
        <InvoiceWeightWarning>
          <AlertCircle aria-hidden="true" />
          <span>
            Invoice qty is {formatWeight(weightShortfall)} less than scheduled.
            Add invoice or reduce scheduled qty.
          </span>
        </InvoiceWeightWarning>
      ) : null}

      {hasInvoices ? (
        <InvoiceSection>
          <InvoiceSectionTitle>
            Invoices for this PO schedule
          </InvoiceSectionTitle>
          <InvoiceList>
            {invoices.map((invoice, invoiceIndex) => (
              <InvoiceDetails
                key={
                  invoice.vendor_invoice_id ||
                  `${invoice.invoice_number}-${invoiceIndex}`
                }
                invoice={invoice}
              />
            ))}
          </InvoiceList>
        </InvoiceSection>
      ) : null}

      <InvoiceAction
        type="button"
        onClick={onManageInvoices}
        aria-label={`${hasInvoices ? "Add or remove" : "Add"} invoices for ${
          purchaseOrder.purchase_order_number
        }`}
      >
        {hasInvoices ? "Add / remove invoices" : "Add invoices"}
        <ChevronRight aria-hidden="true" />
      </InvoiceAction>
    </PurchaseOrderCard>
  );
};

const ScheduleGroupDetails = () => {
  const navigate = useNavigate();
  const { scheduleGroupId } = useParams();
  const [isSelectingInvoices, setIsSelectingInvoices] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(() => new Set());
  const [showEditWarning, setShowEditWarning] = useState(false);
  const {
    getPOScheduleGroupDetails,
    getPOScheduleGroupDetailsStatus,
    poScheduleGroupDetails,
    poScheduleGroupDetailsError,
    shippingNoteSelection,
    setShippingNoteSelection,
    clearShippingNoteSelection,
    planShipmentAsnConfig,
    getPlanShipmentHelpSections,
  } = usePlanShipmentStore();

  const shouldCreateAsn = planShipmentAsnConfig?.should_create_asn === true;

  const shouldShowEditSchedule = (() => {
    const deliveryDateStr = poScheduleGroupDetails?.delivery_date;
    if (!deliveryDateStr) return true;

    const thresholdMins =
      Number(planShipmentAsnConfig?.today_time_threshold_for_schedule_edit_in_mins);

    const now = new Date();
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

    const parsed = new Date(deliveryDateStr);
    if (isNaN(parsed.getTime())) return true;
    const deliveryMidnight = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime();

    // Hide if today is past the delivery date
    if (todayMidnight > deliveryMidnight) return false;

    // If delivery is in the future, always show
    if (todayMidnight < deliveryMidnight) return true;

    // Delivery is today — check time threshold
    if (!Number.isFinite(thresholdMins) || thresholdMins <= 0) return true;
    const currentMins = now.getHours() * 60 + now.getMinutes();
    return currentMins < thresholdMins;
  })();

  const vendorId = window.localStorage.getItem("outlet_id");
  const parsedScheduleGroupId = Number(scheduleGroupId);
  const hasValidVendorId =
    Number.isInteger(Number(vendorId)) && Number(vendorId) > 0;
  const hasValidScheduleGroupId =
    Number.isInteger(parsedScheduleGroupId) && parsedScheduleGroupId > 0;
  const isEditingShippingNote =
    Number(shippingNoteSelection?.schedule_group_id) ===
      parsedScheduleGroupId && Number(shippingNoteSelection?.asn_id) > 0;
  const editingAsnId = isEditingShippingNote
    ? Number(shippingNoteSelection.asn_id)
    : 0;

  const fetchScheduleGroupDetails = useCallback(() => {
    if (!hasValidVendorId || !hasValidScheduleGroupId) return;

    getPOScheduleGroupDetails({
      vendor_id: vendorId,
      schedule_group_id: parsedScheduleGroupId,
      page_size: PAGE_SIZE,
      page_number: 1,
    });
  }, [
    getPOScheduleGroupDetails,
    hasValidScheduleGroupId,
    hasValidVendorId,
    parsedScheduleGroupId,
    vendorId,
  ]);

  useEffect(() => {
    fetchScheduleGroupDetails();
  }, [fetchScheduleGroupDetails]);

  useEffect(() => {
    if (!planShipmentAsnConfig && hasValidVendorId) {
      getPlanShipmentHelpSections({ seller_outlet_id: vendorId });
    } 
  }, []);

  const purchaseOrders = useMemo(
    () =>
      Array.isArray(poScheduleGroupDetails?.purchase_orders)
        ? poScheduleGroupDetails.purchase_orders
        : [],
    [poScheduleGroupDetails]
  );
  const purchaseOrderCount = Number.isFinite(
    Number(poScheduleGroupDetails?.purchase_order_count)
  )
    ? Number(poScheduleGroupDetails.purchase_order_count)
    : purchaseOrders.length;
  const warehouse = poScheduleGroupDetails?.warehouse || {};
  const purchaseOrdersWithInvoices = purchaseOrders.filter(
    (purchaseOrder) =>
      Array.isArray(purchaseOrder.invoices) && purchaseOrder.invoices.length > 0
  );
  const hasInvoicesAvailableForShippingNote = purchaseOrders.some(
    (purchaseOrder) =>
      Array.isArray(purchaseOrder.invoices) &&
      purchaseOrder.invoices.some((invoice) => !hasShippingNote(invoice))
  );
  const isLoading =
    getPOScheduleGroupDetailsStatus === STATUS_BEGIN ||
    !getPOScheduleGroupDetailsStatus;

  useEffect(() => {
    if (
      !isEditingShippingNote ||
      isSelectingInvoices ||
      getPOScheduleGroupDetailsStatus !== STATUS_DONE
    ) {
      return;
    }

    const preSelectedIds = new Set(
      (shippingNoteSelection?.vendor_invoice_ids || [])
        .map(Number)
        .filter((id) => id > 0)
    );
    setSelectedInvoiceIds(preSelectedIds);
    setIsSelectingInvoices(true);
  }, [isEditingShippingNote, isSelectingInvoices, getPOScheduleGroupDetailsStatus, shippingNoteSelection]);

  const handleBack = () => {
    if (isSelectingInvoices && !isEditingShippingNote) {
      setSelectedInvoiceIds(new Set());
      setIsSelectingInvoices(false);
      return;
    }
    clearShippingNoteSelection();
    navigate("/plan-shipment", { state: { activeScheduleTab: "scheduled" } });
  };

  const hasAnyInvoices = purchaseOrders.some(
    (po) => Array.isArray(po.invoices) && po.invoices.length > 0
  );

  const handleEditScheduleClick = () => {
    if (hasAnyInvoices) {
      setShowEditWarning(true);
    } else {
      navigate(`/plan-shipment/schedule-groups/${parsedScheduleGroupId}/edit`, {
        state: { scheduleGroupDeliveryDate: poScheduleGroupDetails?.delivery_date || null },
      });
    }
  };

  const handleStartShippingNote = () => {
    setSelectedInvoiceIds(new Set());
    setIsSelectingInvoices(true);
  };

  const handleSelectInvoice = (vendorInvoiceId) => {
    const invoiceId = Number(vendorInvoiceId);
    if (!Number.isInteger(invoiceId) || invoiceId <= 0) return;

    setSelectedInvoiceIds((currentInvoiceIds) => {
      const nextInvoiceIds = new Set(currentInvoiceIds);
      if (nextInvoiceIds.has(invoiceId)) {
        nextInvoiceIds.delete(invoiceId);
      } else {
        nextInvoiceIds.add(invoiceId);
      }
      return nextInvoiceIds;
    });
  };

  const handleContinueShippingNote = () => {
    if (selectedInvoiceIds.size === 0) return;

    const originalInvoiceIds = [];
    if (editingAsnId) {
      purchaseOrders.forEach((purchaseOrder) => {
        (purchaseOrder.invoices || []).forEach((invoice) => {
          if (Number(invoice.asn_id) === editingAsnId) {
            originalInvoiceIds.push(Number(invoice.vendor_invoice_id));
          }
        });
      });
    }

    setShippingNoteSelection({
      schedule_group_id: parsedScheduleGroupId,
      vendor_invoice_ids: Array.from(selectedInvoiceIds),
      asn_id: editingAsnId,
      original_invoice_ids: originalInvoiceIds,
    });
    navigate(
      `/plan-shipment/schedule-groups/${parsedScheduleGroupId}/shipping-note`
    );
  };

  return (
    <DetailPageShell>
      <DetailPageFrame>
        <DetailHeader>
          <HeaderIconButton
            type="button"
            onClick={handleBack}
            aria-label="Go back"
          >
            <ChevronLeft aria-hidden="true" />
          </HeaderIconButton>
          <DetailHeaderCopy>
            <h1>
              {formatScheduleGroupNumber(
                poScheduleGroupDetails || {
                  schedule_group_id: parsedScheduleGroupId,
                }
              )}
            </h1>
            <span>
              {formatPurchaseOrderCount(purchaseOrderCount)}
              {" • "}
              {formatWeight(poScheduleGroupDetails?.total_weight_in_kg)}
            </span>
          </DetailHeaderCopy>
        </DetailHeader>

        <DetailStatus>
          {formatStatus(poScheduleGroupDetails?.status)}
        </DetailStatus>

        <DetailScrollArea>
          {(!hasValidVendorId || !hasValidScheduleGroupId) && (
            <DetailContent>
              <EmptyState>
                <StateTitle>Unable to load schedule</StateTitle>
                <ErrorMessage>
                  The outlet or PO schedule group is invalid.
                </ErrorMessage>
              </EmptyState>
            </DetailContent>
          )}

          {hasValidVendorId && hasValidScheduleGroupId && isLoading ? (
            <ScheduleGroupDetailsSkeleton />
          ) : null}

          {hasValidVendorId &&
          hasValidScheduleGroupId &&
          getPOScheduleGroupDetailsStatus === STATUS_FAIL ? (
            <DetailContent>
              <EmptyState>
                <StateTitle>Unable to load schedule</StateTitle>
                <ErrorMessage>{poScheduleGroupDetailsError}</ErrorMessage>
                <RetryButton type="button" onClick={fetchScheduleGroupDetails}>
                  Try again
                </RetryButton>
              </EmptyState>
            </DetailContent>
          ) : null}

          {getPOScheduleGroupDetailsStatus === STATUS_DONE &&
          poScheduleGroupDetails ? (
            <DetailContent>
              <GroupSummaryCard>
                <SummaryItem>
                  <SummaryIcon>
                    <CalendarDays aria-hidden="true" />
                  </SummaryIcon>
                  <SummaryLabel>Deliver by</SummaryLabel>
                  <SummaryValue>
                    {formatScheduleDate(poScheduleGroupDetails.delivery_date)}
                  </SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryIcon>
                    <Clock3 aria-hidden="true" />
                  </SummaryIcon>
                  <SummaryLabel>Reporting time</SummaryLabel>
                  <SummaryValue>
                    {formatSlotDisplayName(poScheduleGroupDetails.slot)}
                  </SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryIcon>
                    <MapPin aria-hidden="true" />
                  </SummaryIcon>
                  <SummaryLabel>Deliver at</SummaryLabel>
                  <SummaryValue>
                    {warehouse.warehouse_name
                      ? (() => {
                          const commaIndex = warehouse.warehouse_name.indexOf(",");
                          if (commaIndex === -1) return warehouse.warehouse_name;
                          const code = warehouse.warehouse_name.slice(0, commaIndex).trim();
                          const city = warehouse.warehouse_name.slice(commaIndex + 1).trim();
                          return (
                            <>
                              {code}{city ? "," : ""}
                              {city ? <><br />{city}</> : null}
                            </>
                          );
                        })()
                      : "—"}
                  </SummaryValue>
                  <MapLinkButton
                    latitude={warehouse.latitude}
                    longitude={warehouse.longitude}
                  />
                </SummaryItem>
              </GroupSummaryCard>

              <ScheduledPOHeading>
                Scheduled POs ({purchaseOrderCount})
              </ScheduledPOHeading>

              {(isSelectingInvoices
                ? purchaseOrdersWithInvoices
                : purchaseOrders
              ).length > 0 ? (
                <PurchaseOrderList>
                  {(isSelectingInvoices
                    ? purchaseOrdersWithInvoices
                    : purchaseOrders
                  ).map((purchaseOrder) =>
                    isSelectingInvoices ? (
                      <PurchaseOrderShippingNoteCard
                        key={
                          purchaseOrder.schedule_id ||
                          purchaseOrder.purchase_order_id
                        }
                        purchaseOrder={purchaseOrder}
                        selectedInvoiceIds={selectedInvoiceIds}
                        onSelectInvoice={handleSelectInvoice}
                        editingAsnId={editingAsnId}
                      />
                    ) : (
                      <PurchaseOrderScheduleCard
                        key={
                          purchaseOrder.schedule_id ||
                          purchaseOrder.purchase_order_id
                        }
                        purchaseOrder={purchaseOrder}
                        onManageInvoices={() =>
                          navigate(
                            `/plan-shipment/schedule-groups/${parsedScheduleGroupId}/schedules/${purchaseOrder.schedule_id}/invoices`
                          )
                        }
                      />
                    )
                  )}
                </PurchaseOrderList>
              ) : (
                <EmptyState>
                  <StateTitle>No scheduled POs</StateTitle>
                  <span>
                    No purchase orders were returned for this schedule.
                  </span>
                </EmptyState>
              )}
            </DetailContent>
          ) : null}
        </DetailScrollArea>

        {getPOScheduleGroupDetailsStatus === STATUS_DONE &&
        poScheduleGroupDetails ? (
          <BottomActions>
            {!isSelectingInvoices && shouldShowEditSchedule ? (
              <EditScheduleButton
                type="button"
                onClick={handleEditScheduleClick}
              >
                Edit schedule
              </EditScheduleButton>
            ) : null}
            {shouldCreateAsn ? (
              <CreateShippingNoteButton
                type="button"
                disabled={
                  isSelectingInvoices
                    ? selectedInvoiceIds.size === 0
                    : !hasInvoicesAvailableForShippingNote
                }
                onClick={
                  isSelectingInvoices
                    ? handleContinueShippingNote
                    : handleStartShippingNote
                }
              >
                {isSelectingInvoices
                  ? "Create ASN"
                  : "Create ASN"}
              </CreateShippingNoteButton>
            ) : null}
          </BottomActions>
        ) : null}
      </DetailPageFrame>
      {showEditWarning ? (
        <ModalOverlay
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowEditWarning(false);
          }}
        >
          <ModalSheet>
            <ModalHandle />
            <ModalIconWrapper>
              <img src="/logos/quiz_chat.svg" alt="" aria-hidden="true" style={{ width: 40, height: 40 }} />
            </ModalIconWrapper>
            <ModalTitle style={{ fontSize: 18 }}>Some POs already invoiced!</ModalTitle>
            <ModalSubtitle style={{ fontSize: 12, textAlign: "left", margin: "0 0 20px" }}>
              If PO is edited from the schedule, invoices for that PO will be
              removed too and will have to be added again.
            </ModalSubtitle>
            <ModalButtonRow>
              <ModalCancelButton
                type="button"
                onClick={() => setShowEditWarning(false)}
              >
                Back
              </ModalCancelButton>
              <ModalConfirmButton
                type="button"
                onClick={() => {
                  setShowEditWarning(false);
                  navigate(
                    `/plan-shipment/schedule-groups/${parsedScheduleGroupId}/edit`,
                    { state: { scheduleGroupDeliveryDate: poScheduleGroupDetails?.delivery_date || null } }
                  );
                }}
              >
                Edit anyway
              </ModalConfirmButton>
            </ModalButtonRow>
          </ModalSheet>
        </ModalOverlay>
      ) : null}
    </DetailPageShell>
  );
};

export default ScheduleGroupDetails;
